import {
    bindActionCreators,
    createListenerMiddleware,
    isAnyOf,
} from '@reduxjs/toolkit';
import redPlusSlice, {
    goToBasket,
    goToDeviceSelection,
    setCashback,
    setInitialState,
} from 'Container/App/slice';
import {
    selectCustomerConfig,
    selectMainOffer,
    selectSecondaryOffer,
} from 'Container/App/selectors';
import {
    getSimOnly,
    getTariffWithHardware,
} from 'Container/App/api/glados';
import {
    ADDITIONAL_PAGE_OPTIONS,
    API_BTX_BNT,
    API_BTX_VVL,
    API_SALESCHANNEL_CONSUMER,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
    RedTariff,
    URL_PARAM_SUBGROUP_ID,
    YoungTariff,
} from '@vfde-sails/constants';
import {
    TariffSimOnlyParams,
    TariffWithHardwareParams,
} from '@vfde-sails/glados-v2';
import {
    AppDispatch,
    RootState,
} from '../../store';
import {
    BASKET_PROPOSITION_REDPLUS,
    BASKET_PROPOSITION_VVL,
    createBasket,
    createBasketItem,
    createBasketProposition,
    createSailsReadyEvent,
    generateDeeplink,
} from '@vfde-sails/utils';
import { startAuthentication } from '@vfde-sails/vvl';
import {
    TrackingError,
    TrackingErrorType,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';
import {
    createPageViewTrackingDataWithError,
    createPageViewTrackingData,
} from 'Container/App/helpers/tracking';
import { AppFlow } from 'Container/App/interface';
import { getDiscounts } from 'Container/App/helpers/getDiscounts';

const appListener = createListenerMiddleware();

export default appListener;

export const startAppListening = appListener.startListening.withTypes<
    RootState,
    AppDispatch
>();

/**
 * Matcher is defined as variable so the action type is correctly infered.
 * @see https://github.com/reduxjs/redux-toolkit/discussions/4018
 */
const isSimonlyOrTariffWithHardwareFulfilledMatcher = isAnyOf(
    getSimOnly.matchFulfilled,
    getTariffWithHardware.matchFulfilled,
);

/**
 * See comment above
 */
const isSimonlyOrTariffWithHardwareRejectedMatcher = isAnyOf(
    getSimOnly.matchRejected,
    getTariffWithHardware.matchRejected,
);

export const listeners = {
    setInitialState: () => startAppListening({
        actionCreator: setInitialState,
        effect: (action, listenerApi) => {
            const tariffIds = action.payload.availableSubscriptionIds;
            const customerConfig = action.payload.customerConfig;

            // this request fetches the redplus offers, which requires btx=BNT (even in VVL case)
            listenerApi?.dispatch(getSimOnly.initiate({
                tariffIds,
                btx: API_BTX_BNT,
                salesChannel: [API_SALESCHANNEL_CONSUMER],
            }));

            if (customerConfig.flow === AppFlow.Vvl) {
                listenerApi.dispatch(startAuthentication(true));
            }
        },
    }),
    startAuthenticationFulfilled: () => startAppListening({
        actionCreator: startAuthentication.fulfilled,
        effect: (_action, api) => {
            const customerConfig = selectCustomerConfig(api.getState());

            if (!customerConfig.isXSellEligible) {
                window.location.assign(window[ADDITIONAL_PAGE_OPTIONS].mtan_url);

                return;
            }

            const subscriptionIds = [customerConfig.activeSubscriptionId].filter(Boolean) as Array<RedTariff | YoungTariff>;

            const params: TariffSimOnlyParams | TariffWithHardwareParams = {
                btx: API_BTX_VVL,
                salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[customerConfig.salesChannel]],
                tariffIds: subscriptionIds,
            };

            if (customerConfig.atomicId) {
                api.dispatch(getTariffWithHardware.initiate({
                    ...params,
                    atomicIds: [customerConfig.atomicId],
                }));
            }
            else {
                api.dispatch(getSimOnly.initiate(params));
            }
        },
    }),
    startAuthenticationRejected: () => startAppListening({
        actionCreator: startAuthentication.rejected,
        effect: _action => {
            window.location.assign(window[ADDITIONAL_PAGE_OPTIONS].mtan_url);
        },
    }),
    goToBasket: () => startAppListening({
        actionCreator: goToBasket,
        effect: (action, listenerApi) => {
            const state = listenerApi.getState();

            const customerConfig = selectCustomerConfig(state);
            const mainOffer = selectMainOffer(state);
            const secondaryOffer = selectSecondaryOffer(state);

            if (!mainOffer || (customerConfig.flow === AppFlow.Vvl && !secondaryOffer)) {
                return;
            }

            const discountIds = getDiscounts(state[redPlusSlice.name]);

            const mainOfferItemProperties: Parameters<typeof createBasketItem>[0] = {
                sublevelTariffId: mainOffer.tariffId,
                ...customerConfig.atomicId && { atomicDeviceId: customerConfig.atomicId },
                discountIds,
            };

            if (secondaryOffer) {
                const secondaryOfferItem = createBasketItem({
                    sublevelTariffId: secondaryOffer.tariffId,
                });

                if (action.payload.withRedPlus) {
                    mainOfferItemProperties.redPlusItems = [secondaryOfferItem];
                }
            }

            const mainOfferItem = createBasketItem(mainOfferItemProperties);

            const basketProposition = createBasketProposition(
                customerConfig.flow === AppFlow.Vvl
                    ? BASKET_PROPOSITION_VVL
                    : BASKET_PROPOSITION_REDPLUS,
                [mainOfferItem],
            );
            const basket = createBasket(customerConfig.salesChannel, [basketProposition]);
            const url = generateDeeplink(basket, true);

            window.location.assign(url);
        },
    }),
    goToDeviceSelection: () => startAppListening({
        actionCreator: goToDeviceSelection,
        effect: action => {
            window.location.assign(`${window[ADDITIONAL_PAGE_OPTIONS].redplus_with_hardware_url}?${URL_PARAM_SUBGROUP_ID}=${action.payload}`);
        },
    }),
    handleApiSuccessOrError: () => {
        // listener for case 'all api requests succeeded'
        const unsubscribeAllApiRequestsSucceededListener = startAppListening({
            predicate: (action, currentState) => {
                if (!isSimonlyOrTariffWithHardwareFulfilledMatcher(action)) {
                    return false;
                }

                const customerConfig = selectCustomerConfig(currentState);
                const { flow } = customerConfig;

                if (flow === AppFlow.Standalone && getSimOnly.matchFulfilled(action)) {
                    // BNT/standalone case
                    return true;
                }

                if (flow === AppFlow.Vvl && getTariffWithHardware.matchFulfilled(action)) {
                    // VVL tariff with hardware case
                    return true;
                }

                if (
                    flow === AppFlow.Vvl && getSimOnly.matchFulfilled(action)
                    && (action as any).meta.arg.originalArgs.btx === API_BTX_VVL
                ) {
                    // VVL tariff simonly case
                    return true;
                }

                return false;
            },
            effect: (_action, api) => {
                // unsubscribe immediately because we only want to expose public functions once and track success pageview once
                api.unsubscribe();

                // Expose public functions
                const actions = bindActionCreators({
                    setCashback,
                }, api.dispatch);

                createSailsReadyEvent([
                    {
                        actions: {
                            setCashback: actions.setCashback,
                        },
                        applicationScope: 'FAMILYCARD',
                    },
                ]);

                const state = api.getState();
                const customerConfig = selectCustomerConfig(state);

                // Track page view
                trackIt(
                    TrackType.PageView,
                    createPageViewTrackingData({
                        flow: customerConfig.flow,
                        salesChannel: customerConfig.salesChannel,
                        pageName: window[ADDITIONAL_PAGE_OPTIONS].pageName,
                    }),
                );
            },
        });

        // listener for case 'any api request failed'
        const unsubscribeAnyApiRequestsFailedListener = startAppListening({
            matcher: isSimonlyOrTariffWithHardwareRejectedMatcher,
            effect: (action, api) => {
                // unsubscribe immediately because we only want to track one error pageview
                api.unsubscribe();
                // also unsubscribe the handler for the success case
                // to avoid success pageview tracking if an earlier request failed
                // (i. e. the first request failed, but the second request succeeded)
                unsubscribeAllApiRequestsSucceededListener();

                const customerConfig = selectCustomerConfig(api.getState());

                const trackingData = {
                    flow: customerConfig.flow,
                    salesChannel: customerConfig.salesChannel,
                    pageName: window[ADDITIONAL_PAGE_OPTIONS].pageName,
                };

                const trackingError: TrackingError = {
                    /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                    error_type: TrackingErrorType.Technical,
                    error_message: action.payload!.message,
                    error_code: action.payload!.status?.toString(),
                    error_location: action.meta.arg.endpointName,
                    /* eslint-enable camelcase, @typescript-eslint/naming-convention */
                };

                trackIt(
                    TrackType.PageView,
                    createPageViewTrackingDataWithError(
                        trackingData,
                        trackingError,
                    ),
                );
            },
        });

        return () => {
            unsubscribeAllApiRequestsSucceededListener();
            unsubscribeAnyApiRequestsFailedListener();
        };
    },
};

/**
 * Starts all listeners
 */
export const startListeners = () => {
    const unsubscribeListeners = Object.values(listeners).map(listener => listener());

    return () => {
        unsubscribeListeners.forEach(unsubscribeListener => unsubscribeListener());
    };
};
